import React from 'react'
import { createTheme, ThemeProvider } from '@mui/material'

import circlesOrbitColorPalette from './circlesColorTheme'

declare module '@mui/material/styles' {
  interface PaletteOptions {
    accent: NewPaletteColorOptions
    neutrals: NewPaletteColorOptions
  }
  interface NewPaletteColorOptions {
    main?: string
    light?: string
    secondary?: string
    0?: string
    100?: string
    200?: string
    300?: string
    400?: string
    500?: string
    600?: string
    700?: string
    800?: string
    900?: string
  }
}

declare module '@mui/material/Button/Button' {
  interface ButtonPropsVariantOverrides {
    filled: true
    secondary: true
    noBackground: true
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    note: true
  }
}
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    listCard: true
  }
}

declare module '@mui/material/TableHead' {
  interface TableHeadPropsVariantOverrides {
    plain: true
  }
}

const circlesColorPaletteTheme = createTheme({
  palette: {
    //from light to dark primary colors
    ...circlesOrbitColorPalette
  }
})

export const circlesTheme = createTheme(
  {
    typography: {
      fontFamily: 'Roboto, sans-serif'
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          // The props to apply
          disableRipple: true // No more ripple, on the whole application 💣!
        }
      },
      // Name of the component
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            height: '40px',
            border: '2px solid #0000FF',
            fontWeight: '700',
            padding: '12px 24px',
            fontSize: '16px',
            textTransform: 'none',
            margin: '4px',
            '&:disabled': {
              cursor: 'not-allowed'
            }
          }
        },
        variants: [
          {
            props: { variant: 'filled' },
            style: {
              color: 'white',
              borderRadius: '25px',
              backgroundColor: circlesOrbitColorPalette.primary.main,
              '&:hover': {
                backgroundColor: circlesOrbitColorPalette.primary.dark,
                color: 'white',
                border: `2px solid ${circlesOrbitColorPalette.primary.dark}`
              },
              '&:active': {
                backgroundColor: circlesOrbitColorPalette.primary.dark,
                color: 'white',
                border: `2px solid ${circlesOrbitColorPalette.primary.dark}`
              },
              '&:disabled': {
                border: `2px solid white`,
                backgroundColor: circlesOrbitColorPalette.primary.disabled,
                color: `#FFF`
              }
            }
          },
          {
            props: { variant: 'secondary' },
            style: {
              color: '#0000FF',
              border: `2px solid ${circlesOrbitColorPalette.primary.main}`,
              backgroundColor: '#FFF',
              '&:hover': {
                color: 'white',
                backgroundColor: `${circlesOrbitColorPalette.primary.main}`,
                border: `2px solid ${circlesOrbitColorPalette.primary.main}`
              },
              '&:active': {
                backgroundColor: `${circlesOrbitColorPalette.primary.main}`,
                border: `2px solid ${circlesOrbitColorPalette.primary.main}`
              },
              '&:disabled': {
                border: `2px solid ${circlesOrbitColorPalette.primary.disabled}`,
                backgroundColor: 'white',
                color: `${circlesOrbitColorPalette.primary.disabled}`
              }
            }
          },
          {
            props: { variant: 'noBackground' },
            style: {
              border: `none`,
              color: circlesOrbitColorPalette.primary.main,
              backgroundColor: 'transparent',
              padding: '0',
              margin: '0',
              '&:hover': {
                backgroundColor: 'transparent'
              },
              '&:active': {
                backgroundColor: 'transparent'
              }
            }
          }
        ]
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            height: '20px',
            width: '20px',
            borderRadius: '4px',
            color: '#0000FF'
          }
        }
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: circlesOrbitColorPalette.primary.main,
            color: 'white'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: 'white',
            borderRadius: '8px'
          }
        }
      },

      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: circlesOrbitColorPalette.neutrals.light
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            'th:first-of-type': {
              borderTopLeftRadius: '8px'
            },
            'th:last-of-type': {
              borderTopRightRadius: '8px'
            },
            '&.plainHeader': {
              backgroundColor: 'transparent'
            }
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            'tr:last-of-type td:first-of-type': {
              borderBottomLeftRadius: '8px'
            },
            'tr:last-of-type td:last-of-type': {
              borderBottomRightRadius: '8px'
            }
          }
        }
      },
      MuiTypography: {
        variants: [
          {
            props: { variant: 'note' },
            style: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              color: circlesOrbitColorPalette.neutrals[500],
              fontWeight: '600'
            }
          }
        ],
        styleOverrides: {
          root: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '16px',
            margin: '8px 4px',
            color: circlesOrbitColorPalette.primary.dark,
            lineHeight: '28px'
          },
          subtitle1: {
            fontSize: '14px'
          },
          subtitle2: {
            fontSize: '12px'
          },
          h2: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '48px',
            fontWeight: '600'
          },
          h3: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '36px',
            fontWeight: '600'
          },
          h4: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '24px',
            fontWeight: '600'
          },
          h5: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: '600'
          },
          h6: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: '16px',
            fontWeight: '600'
          }
        }
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            'li button': {
              fontWeight: '700',
              fontSize: '18px',
              lineHeight: '21px',
              border: `1px solid ${circlesOrbitColorPalette.neutrals[300]}`,
              color: circlesOrbitColorPalette.neutrals[300]
            },
            '&& .Mui-selected': {
              border: `1px solid ${circlesOrbitColorPalette.primary.main}`,
              color: circlesOrbitColorPalette.primary.main,
              backgroundColor: 'white'
            },
            'li:first-of-type button': {
              border: 'None'
            },
            'li:last-of-type button': {
              border: 'None'
            }
          }
        }
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            backgroundColor: 'white',
            borderColor: circlesOrbitColorPalette.primary.main,
            color: circlesOrbitColorPalette.primary.main,
            '&& .Mui-selected': {
              backgroundColor: circlesOrbitColorPalette.primary.main,
              borderWidth: '2px',
              color: '#fff'
            }
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            padding: '8px 0px 0px',
            fontFamily: 'Roboto,sans-serif',
            textTransform: 'capitalize',
            '&.Mui-selected': {
              color: circlesOrbitColorPalette.primary.dark
            }
          }
        }
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            color: circlesOrbitColorPalette.primary.disabled,
            fontSize: '14px',
            width: '380px',
            '& .MuiSlider-thumb': {
              backgroundColor: '#0000FF'
            },
            'a:hover': {
              textDecoration: 'underline'
            },
            ':last-of-type a': {
              fontWeight: '700'
            }
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            'p:active, a:active': {
              fontSize: '18px',
              textDecoration: 'underline',
              color: circlesOrbitColorPalette.primary.disabled,
              lineheight: '21px',
              fontWeight: '900'
            }
          }
        }
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            '& .MuiSlider-thumb': {
              boxShadow: 'none',
              color: '#3DA9FC'
            },
            '&.MuiSlider-colorPrimary': {
              color: '#3DA9FC'
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            fontSize: '18px',
            textDecoration: 'none',
            color: circlesOrbitColorPalette.primary.light,
            lineheight: '21px',
            fontWeight: '600',
            cursor: 'pointer',
            '&:hover': {
              color: circlesOrbitColorPalette.primary.light
            },
            '&:active': {
              color: circlesOrbitColorPalette.primary.dark
            },
            '&:disabled': {
              cursor: 'not-allowed',
              color: '#A4AAFF'
            }
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: '400',
            fontFamily: 'Roboto',
            color: circlesOrbitColorPalette.neutrals[700],
            '&.formSectionHeader': {
              color: circlesOrbitColorPalette.primary.dark,
              fontWeight: 600
            },
            '&.formSmallLabel': {
              fontSize: '14px',
              color: circlesOrbitColorPalette.neutrals[500]
            }
          }
        }
      },
      MuiCard: {
        variants: [
          {
            props: { variant: 'listCard' },
            style: {
              fontFamily: 'Roboto, sans-serif',
              margin: '16px',
              border: `1px solid ${circlesOrbitColorPalette.neutrals[200]}`
            }
          }
        ]
      }
    }
  },
  circlesColorPaletteTheme
)

type ChildrenProps = {
  children: React.ReactNode
}

export default function CirclesPalette({ children }: ChildrenProps) {
  return <ThemeProvider theme={circlesTheme}> {children} </ThemeProvider>
}
